import { Component, forwardRef, Input, OnChanges, SimpleChanges } from '@angular/core';
import {
    AbstractControl,
    ControlValueAccessor,
    FormsModule,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    ValidationErrors,
} from '@angular/forms';
import {
    NgbDateParserFormatter,
    NgbDateStruct,
    NgbDatepickerModule,
} from '@ng-bootstrap/ng-bootstrap';
import { CommonModule } from '@angular/common';
import { EdDmyParserFormatter } from './ngbd-datepicker.adapter';

@Component({
    selector: 'ed-datepicker',
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, NgbDatepickerModule],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => EdDatepickerComponent),
            multi: true,
        },
        {
            provide: NgbDateParserFormatter,
            useClass: EdDmyParserFormatter,
        },
    ],
    templateUrl: './ed-datepicker.component.html',
})
export class EdDatepickerComponent implements ControlValueAccessor {
    /** Valeur interne (format NgbDateStruct) */
    model: NgbDateStruct | null = null;

    /** Placeholder de l’input */
    @Input() placeholder = 'dd/mm/yyyy';

    /** État disabled (mis à jour via setDisabledState) */
    disabled = false;

    // ---- CVA handlers ----
    onChange: (value: string | null) => void = () => {};
    onTouched: () => void = () => {};

    writeValue(value: string | Date | NgbDateStruct | null): void {
        if (!value) {
            this.model = null;
            return;
        }

        if (value instanceof Date) {
            this.model = this.dateToStruct(value);
            return;
        }

        if (typeof value === 'string') {
            const parsed = this.tryParseYmd(value) ?? this.tryParseDmy(value);
            this.model = parsed;
            return;
        }

        this.model = value;
    }

    registerOnChange(fn: (value: string | null) => void): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void): void {
        this.onTouched = fn;
    }

    setDisabledState(isDisabled: boolean): void {
        this.disabled = !!isDisabled;
    }

    // ---- Handlers UI ----
    onDateChanged(model: NgbDateStruct | string | null) {
        if (!model) {
            this.model = null;
        }
        if (typeof model === 'string') {
            this.model = this.tryParseDmy(model);
        } else if (model?.day) {
            this.model = model;
        }

        const out = this.model ? this.structToDmy(this.model) : null;
        this.onTouched();
        this.onChange(out);
    }

    // ---- Helpers ----
    private pad(n: number) {
        return String(n).padStart(2, '0');
    }

    private structToDmy(s: NgbDateStruct): string | null {
        if (!s || !s.day) return null;
        return `${s.year}-${this.pad(s.month)}-${this.pad(s.day)}`;
    }

    private dateToStruct(d: Date): NgbDateStruct {
        return { year: d.getFullYear(), month: d.getMonth() + 1, day: d.getDate() };
    }
    private tryParseYmd(v: string): NgbDateStruct | null {
        const m = /^(\d{2}|\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})$/.exec(v.trim());
        if (!m) return null;

        let year = +m[1];
        const month = +m[2];
        const day = +m[3];

        if (year < 100) year += year < 50 ? 2000 : 1900;
        if (month < 1 || month > 12 || day < 1 || day > 31) return null;

        return { year, month, day };
    }

    private tryParseDmy(v: string): NgbDateStruct | null {
        const m = /^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/.exec(v.trim());
        if (!m) return null;

        const day = +m[1];
        const month = +m[2];
        let year = +m[3];

        // Convertit 2 chiffres en 20xx (simple et suffisant dans 99% des cas)
        if (year < 100) {
            year += year < 50 ? 2000 : 1900; // 25 -> 2025, 85 -> 1985
        }

        // Validation minimale
        if (month < 1 || month > 12 || day < 1 || day > 31) return null;

        return { year, month, day };
    }
}

export const dateFormatValidator = () => {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;
        if (!value) return null; // rien à valider

        // formats acceptés : YYYY-MM-DD ou DD/MM/YYYY (souples)
        const ymd = /^\d{4}-\d{1,2}-\d{1,2}$/;
        const dmy = /^\d{1,2}[\/\-]\d{1,2}[\/\-](\d{2}|\d{4})$/;

        return ymd.test(value) || dmy.test(value) ? null : { invalidDateFormat: true };
    };
};
